import { PendingButton } from "@/components/common/PendingButton/PendingButton"
import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { toastApiError } from "@/features/material-compliance/api/apiError"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useRemoveMember } from "@/services/api/teamService"
import { TeamMember } from "@/types/team"
import { getTeamErrorCode } from "./teamErrors"

interface RemoveMemberDialogProps {
  member: TeamMember | null
  onClose: () => void
}

export function RemoveMemberDialog({
  member,
  onClose,
}: RemoveMemberDialogProps) {
  const { translate } = useTranslation()
  const removeMutation = useRemoveMember()

  const handleConfirm = () => {
    if (!member) return
    removeMutation.mutate(member.id, {
      onSuccess: () => {
        toast.success(translate("TEAM/REMOVE_SUCCESS"))
        onClose()
      },
      onError: (err) => {
        // A 5xx is already toasted by the API client.
        if (getTeamErrorCode(err) === "LAST_ADMIN")
          toast.error(translate("TEAM/LAST_ADMIN"))
        else toastApiError(err, translate, "TEAM/REMOVE_ERROR")
      },
    })
  }

  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {translate("TEAM/REMOVE_TITLE", { email: member?.email ?? "" })}
          </DialogTitle>
          <DialogDescription>
            {translate("TEAM/REMOVE_DESCRIPTION")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {translate("TEAM/CANCEL")}
          </Button>
          <PendingButton
            type="button"
            variant="destructive"
            pending={removeMutation.isPending}
            onClick={handleConfirm}
          >
            {translate("TEAM/REMOVE_CONFIRM")}
          </PendingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
